import { useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
} from "@material-ui/icons";

const MODULE_OPTIONS = [
  { value: "stock_management", label: "Stock Management" },
  { value: "qr_serial", label: "QR Code / Verification" },
  { value: "campaigns", label: "Campaigns & Loyalty" },
  { value: "dealers", label: "Dealer Management" },
  { value: "reports", label: "Reports & Analytics" },
  { value: "account_billing", label: "Account & Settings" },
  { value: "other", label: "Other" },
];

const CreateTicketPage = ({
  companies,
  companiesLoading,
  companySearch,
  onCompanySearchChange,
  onCompanyChange,
  selectedCompany,
  users,
  usersLoading,
  userSearch,
  onUserSearchChange,
  isSubmitting,
  onSubmit,
  onCancel,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [smartkoodModule, setSmartKoodModule] = useState("");
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!selectedCompany) newErrors.company = "Company is required";
    if (!selectedUser) newErrors.user = "Reporter is required";
    if (!title || title.length < 10) newErrors.title = "Subject must be at least 10 characters";
    if (!description || description.length < 20) newErrors.description = "Description must be at least 20 characters";
    if (!smartkoodModule) newErrors.smartkood_module = "Category is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      company_id: selectedCompany.id || selectedCompany.uuid,
      reporter_id: selectedUser.id || selectedUser.uuid,
      reporter_name: selectedUser.name || `${selectedUser.first_name || ""} ${selectedUser.last_name || ""}`.trim(),
      reporter_email: selectedUser.email,
      reporter_company_name: selectedCompany.name,
      title,
      description,
      smartkood_module: smartkoodModule,
      attachments: [],
    });
  };

  const handleCompanySelect = (_, value) => {
    onCompanyChange(value);
    setSelectedUser(null);
  };

  const getUserLabel = (user) => {
    const name = user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return name ? `${name} (${user.email})` : user.email;
  };

  return (
    <>
      <Helmet>
        <title>Create Ticket | Kood Asia Management Portal</title>
      </Helmet>
      <Box sx={{ backgroundColor: "background.default", minHeight: "100%", py: 3 }}>
        <Container maxWidth={false}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onCancel}
              sx={{ textTransform: "none", color: "#64748B", fontSize: 14 }}
            >
              Back to Tickets
            </Button>
          </Box>

          <Card sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ padding: 2 }}>
              Create Ticket on Behalf of Customer
            </Typography>
            <Typography variant="body2" sx={{ px: 2, pb: 2, color: "text.secondary" }}>
              Select a company and user, then fill in the ticket details.
            </Typography>
          </Card>

          <Card sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              {/* Company Selector */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054", mb: 1 }}>
                  Company *
                </Typography>
                <Autocomplete
                  options={companies}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedCompany}
                  onChange={handleCompanySelect}
                  inputValue={companySearch}
                  onInputChange={(_, value) => onCompanySearchChange(value)}
                  loading={companiesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="Search company..."
                      error={!!errors.company}
                      helperText={errors.company}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {companiesLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* User Selector */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054", mb: 1 }}>
                  Reporter (User) *
                </Typography>
                <Autocomplete
                  options={users}
                  getOptionLabel={getUserLabel}
                  value={selectedUser}
                  onChange={(_, value) => setSelectedUser(value)}
                  inputValue={userSearch}
                  onInputChange={(_, value) => onUserSearchChange(value)}
                  loading={usersLoading}
                  disabled={!selectedCompany}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={selectedCompany ? "Search user..." : "Select a company first"}
                      error={!!errors.user}
                      helperText={errors.user}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {option.name || `${option.first_name || ""} ${option.last_name || ""}`.trim()}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                          {option.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Box>

              {/* Category */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054", mb: 1 }}>
                  Category *
                </Typography>
                <FormControl variant="outlined" size="small" fullWidth error={!!errors.smartkood_module}>
                  <Select
                    value={smartkoodModule}
                    onChange={(e) => setSmartKoodModule(e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      selected
                        ? MODULE_OPTIONS.find((m) => m.value === selected)?.label || selected
                        : <span style={{ color: "#94A3B8" }}>Select a category...</span>
                    }
                  >
                    {MODULE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.smartkood_module && (
                    <FormHelperText>{errors.smartkood_module}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* Subject */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054", mb: 1 }}>
                  Subject *
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Brief summary of the issue..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                />
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054", mb: 1 }}>
                  Description *
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Describe the issue in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Box>

              {/* Actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  borderTop: "1px solid #F1F5F9",
                  pt: 3,
                }}
              >
                <Button
                  onClick={onCancel}
                  disabled={isSubmitting}
                  sx={{
                    textTransform: "none",
                    color: "#64748B",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    px: 3,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  endIcon={
                    isSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendIcon style={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#5A9559",
                    borderRadius: "8px",
                    px: 3,
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#4A8449" },
                  }}
                >
                  {isSubmitting ? "Creating..." : "Create Ticket"}
                </Button>
              </Box>
            </form>
          </Card>
        </Container>
      </Box>
    </>
  );
};

CreateTicketPage.propTypes = {
  companies: PropTypes.array,
  companiesLoading: PropTypes.bool,
  companySearch: PropTypes.string,
  onCompanySearchChange: PropTypes.func.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  selectedCompany: PropTypes.object,
  users: PropTypes.array,
  usersLoading: PropTypes.bool,
  userSearch: PropTypes.string,
  onUserSearchChange: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CreateTicketPage;
